import {
  isRecord,
  normalizeMessengerConversation,
  normalizeMessengerMessage,
  normalizeMessengerReadReceipt,
  readStringValue
} from './messengerNormalizers';
import type {
  MessengerConversation,
  MessengerMessage,
  MessengerReadReceipt,
  MessengerUser
} from './types';

export interface MessengerStoreState {
  conversations: MessengerConversation[];
  activeConversationId?: string;
  messagesByConversationId: Record<string, MessengerMessage[]>;
  users: MessengerUser[];
  totalConversations?: number;
  totalUsers?: number;
  isConversationsLoading: boolean;
  isMessagesLoading: boolean;
  isUsersLoading: boolean;
  isSending: boolean;
  error?: string;
  usersError?: string;
}

export const messengerStoreUpdatedEvent = 'hsdesk-messenger-store-updated';

const initialMessengerStoreState: MessengerStoreState = {
  conversations: [],
  messagesByConversationId: {},
  users: [],
  isConversationsLoading: false,
  isMessagesLoading: false,
  isUsersLoading: false,
  isSending: false
};

let messengerState: MessengerStoreState = initialMessengerStoreState;

function emitMessengerStoreUpdate() {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(messengerStoreUpdatedEvent));
}

function setMessengerState(
  updater:
    | MessengerStoreState
    | ((current: MessengerStoreState) => MessengerStoreState)
) {
  messengerState =
    typeof updater === 'function' ? updater(messengerState) : updater;
  emitMessengerStoreUpdate();
}

function getConversationTime(conversation: MessengerConversation) {
  return new Date(
    conversation.updatedAt ??
      conversation.lastMessageAt ??
      conversation.createdAt ??
      0
  ).getTime();
}

function sortConversations(conversations: MessengerConversation[]) {
  return [...conversations].sort(
    (first, second) => getConversationTime(second) - getConversationTime(first)
  );
}

function getMessageTime(message: MessengerMessage) {
  return new Date(message.createdAt).getTime();
}

function sortMessages(messages: MessengerMessage[]) {
  return [...messages].sort(
    (first, second) => getMessageTime(first) - getMessageTime(second)
  );
}

function sameMessage(left: MessengerMessage, right: MessengerMessage) {
  return (
    left.id === right.id ||
    Boolean(
      left.clientMessageId &&
        right.clientMessageId &&
        left.clientMessageId === right.clientMessageId
    )
  );
}

function mergeConversation(
  current: MessengerConversation | undefined,
  next: MessengerConversation
): MessengerConversation {
  if (!current) return next;

  return {
    ...current,
    ...next,
    title: next.title ?? current.title,
    participants: next.participants.length
      ? next.participants
      : current.participants,
    lastMessage: next.lastMessage ?? current.lastMessage,
    lastMessageAt: next.lastMessageAt ?? current.lastMessageAt,
    updatedAt: next.updatedAt ?? current.updatedAt,
    createdAt: next.createdAt ?? current.createdAt
  };
}

function upsertConversationInList(
  conversations: MessengerConversation[],
  conversation: MessengerConversation
) {
  const existing = conversations.find((item) => item.id === conversation.id);
  const merged = mergeConversation(existing, conversation);

  return sortConversations([
    merged,
    ...conversations.filter((item) => item.id !== conversation.id)
  ]);
}

function getConversationIdFromPayload(payload: unknown) {
  if (!isRecord(payload)) {
    return undefined;
  }

  return readStringValue(payload, ['conversationId', 'chatConversationId']);
}

export function getMessengerStoreSnapshot() {
  return messengerState;
}

export function subscribeMessengerStore(listener: () => void) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  window.addEventListener(messengerStoreUpdatedEvent, listener);

  return () => {
    window.removeEventListener(messengerStoreUpdatedEvent, listener);
  };
}

export function clearMessengerStore() {
  setMessengerState(initialMessengerStoreState);
}

export function setMessengerConversationsLoading(isLoading: boolean) {
  setMessengerState((current) => ({
    ...current,
    isConversationsLoading: isLoading
  }));
}

export function setMessengerMessagesLoading(isLoading: boolean) {
  setMessengerState((current) => ({
    ...current,
    isMessagesLoading: isLoading
  }));
}

export function setMessengerUsersLoading(isLoading: boolean) {
  setMessengerState((current) => ({
    ...current,
    isUsersLoading: isLoading
  }));
}

export function setMessengerSending(isSending: boolean) {
  setMessengerState((current) => ({
    ...current,
    isSending
  }));
}

export function setMessengerError(error?: string) {
  setMessengerState((current) => ({
    ...current,
    error
  }));
}

export function setMessengerUsersError(usersError?: string) {
  setMessengerState((current) => ({
    ...current,
    usersError
  }));
}

export function setMessengerConversations(
  conversations: MessengerConversation[],
  totalConversations?: number
) {
  setMessengerState((current) => ({
    ...current,
    conversations: sortConversations(conversations),
    totalConversations,
    error: undefined
  }));
}

export function setMessengerUsers(users: MessengerUser[], totalUsers?: number) {
  setMessengerState((current) => ({
    ...current,
    users,
    totalUsers,
    usersError: undefined
  }));
}

export function setActiveMessengerConversation(conversationId?: string) {
  setMessengerState((current) => ({
    ...current,
    activeConversationId: conversationId
  }));
}

export function upsertMessengerConversation(
  conversation: MessengerConversation
) {
  setMessengerState((current) => ({
    ...current,
    conversations: upsertConversationInList(current.conversations, conversation)
  }));
}

export function removeMessengerConversation(conversationId: string) {
  setMessengerState((current) => {
    const { [conversationId]: removedMessages, ...messagesByConversationId } =
      current.messagesByConversationId;
    void removedMessages;

    return {
      ...current,
      activeConversationId:
        current.activeConversationId === conversationId
          ? undefined
          : current.activeConversationId,
      messagesByConversationId,
      conversations: current.conversations.filter(
        (conversation) => conversation.id !== conversationId
      )
    };
  });
}

export function setMessengerMessages(
  conversationId: string,
  messages: MessengerMessage[]
) {
  setMessengerState((current) => ({
    ...current,
    messagesByConversationId: {
      ...current.messagesByConversationId,
      [conversationId]: sortMessages(messages)
    },
    error: undefined
  }));
}

export function appendMessengerMessage(message: MessengerMessage) {
  setMessengerState((current) => {
    const currentMessages =
      current.messagesByConversationId[message.conversationId] ?? [];
    const nextMessages = sortMessages([
      message,
      ...currentMessages.filter((item) => !sameMessage(item, message))
    ]);
    const existingConversation = current.conversations.find(
      (conversation) => conversation.id === message.conversationId
    );
    const conversationUpdate: MessengerConversation = {
      id: message.conversationId,
      title: existingConversation?.title,
      participants: existingConversation?.participants ?? [],
      lastMessage: message,
      lastMessageAt: message.createdAt,
      updatedAt: message.createdAt,
      unreadCount: existingConversation?.unreadCount ?? 0
    };

    return {
      ...current,
      messagesByConversationId: {
        ...current.messagesByConversationId,
        [message.conversationId]: nextMessages
      },
      conversations: upsertConversationInList(
        current.conversations,
        conversationUpdate
      )
    };
  });
}

export function markMessengerMessageFailed(
  conversationId: string,
  clientMessageId: string
) {
  setMessengerState((current) => {
    const messages = current.messagesByConversationId[conversationId] ?? [];

    return {
      ...current,
      messagesByConversationId: {
        ...current.messagesByConversationId,
        [conversationId]: messages.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, status: 'failed' }
            : message
        )
      }
    };
  });
}

export function markMessengerConversationRead(receipt: MessengerReadReceipt) {
  setMessengerState((current) => ({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== receipt.conversationId) {
        return conversation;
      }

      return {
        ...conversation,
        unreadCount: receipt.unreadCount ?? 0,
        participants: conversation.participants.map((participant) =>
          !receipt.userId || participant.userId === receipt.userId
            ? {
                ...participant,
                readAt: receipt.readAt ?? participant.readAt,
                lastReadMessageId:
                  receipt.lastReadMessageId ?? participant.lastReadMessageId
              }
            : participant
        )
      };
    })
  }));
}

export function applyRealtimeChatMessage(
  payload: unknown,
  currentUserId?: string
) {
  const conversation = normalizeMessengerConversation(payload, currentUserId);
  const conversationId =
    conversation?.id ?? getConversationIdFromPayload(payload);
  const message = normalizeMessengerMessage(
    payload,
    conversationId,
    currentUserId
  );

  if (conversation) {
    upsertMessengerConversation(conversation);
  }

  if (!message) {
    return;
  }

  appendMessengerMessage(message);

  setMessengerState((current) => ({
    ...current,
    conversations: current.conversations.map((item) => {
      if (item.id !== message.conversationId) {
        return item;
      }

      const shouldIncrementUnread =
        current.activeConversationId !== message.conversationId &&
        !message.isMine;

      return {
        ...item,
        unreadCount: shouldIncrementUnread ? item.unreadCount + 1 : 0,
        lastMessage: message,
        lastMessageAt: message.createdAt,
        updatedAt: message.createdAt
      };
    })
  }));
}

export function applyRealtimeChatConversationUpdated(
  payload: unknown,
  currentUserId?: string
) {
  const conversation = normalizeMessengerConversation(payload, currentUserId);

  if (!conversation) {
    return;
  }

  if (conversation.isDeleted) {
    removeMessengerConversation(conversation.id);
    return;
  }

  upsertMessengerConversation(conversation);
}

export function applyRealtimeChatConversationRead(payload: unknown) {
  const receipt = normalizeMessengerReadReceipt(payload);

  if (receipt) {
    markMessengerConversationRead(receipt);
  }
}
